import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeftRight, Download } from "lucide-react";
import { exportCSV } from "../../lib/csvExport";

export default function TransfersHistoryDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [persons, setPersons] = useState([]);
  const [filters, setFilters] = useState({
    action: "",        // '' | 'pickup' | 'handover'
    person_id: "",
    from_date: "",
    to_date: "",
    q: "",
  });

  useEffect(() => {
    api.get("/persons").then((r) => setPersons(r.data || [])).catch(() => setPersons([]));
  }, []);

  const load = async () => {
    setRows(null);
    const params = {};
    if (filters.action) params.action = filters.action;
    if (filters.person_id) params.person_id = filters.person_id;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    if (filters.q) params.q = filters.q;
    try {
      const { data } = await api.get("/inventory/transfers", { params });
      setRows(data || []);
    } catch {
      toast.error("Eroare la încărcare");
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const exportCsv = () => {
    if (!rows || rows.length === 0) return;
    const header = ["Data", "Acțiune", "Echipament", "Efectuat de", "Cealaltă parte", "Notă"];
    const csv = [header.join(";")];
    for (const r of rows) {
      const vals = [
        String(r.ts || "").slice(0, 19).replace("T", " "),
        r.action === "pickup" ? "Preia" : r.action === "handover" ? "Predă" : r.action,
        (r.item_name || "").replaceAll(";", ","),
        (r.actor_name || "").replaceAll(";", ","),
        (r.counterparty_name || "").replaceAll(";", ","),
        (r.note || "").replaceAll(";", ",").replaceAll("\n", " "),
      ];
      csv.push(vals.map((v) => `"${v}"`).join(";"));
    }
    const blob = new Blob(["\uFEFF" + csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `istoric-transferuri-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="transfers-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight size={18} className="text-cyan-600"/> Istoric transferuri echipamente
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
          <div>
            <Label className="small-label">Acțiune</Label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-xs"
              data-testid="transfers-filter-action"
            >
              <option value="">Toate</option>
              <option value="pickup">📥 Preia</option>
              <option value="handover">📤 Predă</option>
            </select>
          </div>
          <div>
            <Label className="small-label">Angajat</Label>
            <select
              value={filters.person_id}
              onChange={(e) => setFilters({ ...filters, person_id: e.target.value })}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-xs"
              data-testid="transfers-filter-person"
            >
              <option value="">Toți</option>
              {persons.map((p) => (
                <option key={p.id} value={p.id}>{p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim()}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="small-label">De la data</Label>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="h-9 text-xs"/>
          </div>
          <div>
            <Label className="small-label">Până la data</Label>
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="h-9 text-xs"/>
          </div>
          <div>
            <Label className="small-label">Căutare</Label>
            <Input
              placeholder="Echipament / persoană…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="h-9 text-xs"
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>{rows === null ? "Se încarcă…" : `${rows.length} înregistrări`}</span>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows || rows.length === 0}>
            <Download size={12} className="mr-1"/> Export CSV
          </Button>
        </div>

        <div className="border border-zinc-200 rounded-md max-h-[420px] overflow-y-auto">
          {rows === null ? (
            <div className="p-6 text-center text-sm text-zinc-400">Se încarcă…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">Niciun transfer înregistrat pentru filtrele selectate.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr className="text-left text-[10px] uppercase text-zinc-500 tracking-wider">
                  <th className="px-3 py-2 font-bold">Data</th>
                  <th className="px-3 py-2 font-bold">Acțiune</th>
                  <th className="px-3 py-2 font-bold">Echipament</th>
                  <th className="px-3 py-2 font-bold">Efectuat de</th>
                  <th className="px-3 py-2 font-bold">Cealaltă parte</th>
                  <th className="px-3 py-2 font-bold">Notă</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-zinc-50" data-testid={`transfer-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{String(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${
                        r.action === "pickup" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"
                      }`}>
                        {r.action === "pickup" ? "📥 Preia" : "📤 Predă"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-semibold">{r.item_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-600">{r.actor_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-600">{r.counterparty_name || <span className="text-zinc-400 italic">—</span>}</td>
                    <td className="px-3 py-2 text-zinc-500 italic">{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
